import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import type { ChatListType } from '@/types/ChatListItem'
import type {
	Channel,
	ChannelDetail,
	Session,
	Message,
	ChannelMember
} from '@/types/Chat'
import type {
	ChannelListResponse,
	ChannelListSearchResponse,
	ChannelMessagesSearchResponse,
	EnterChannelResponse
} from '@/types/Api'
import type {
	ChannelMessagesSearchResultItem,
	ChannelMessagesSearchResultItemFlat
} from '@/types/Search'
import { nextRender } from '@/services/nextRender'
import { useRoute } from 'vue-router'
import signalRService from '@/services/signalr'
import { useToast } from 'vue-toastification'
import { useUserStore } from './userStore'

export const useChatStore = defineStore('chat', () => {
	const route = useRoute()
	const userStore = useUserStore()
	const toast = useToast()

	/**
	 * currently opened channel info
	 */
	const currentChannel = ref<ChannelDetail | null>(null)

	/**
	 * id of current channel parsed from url
	 */
	const currentChannelId = computed<number>(() => {
		return Number(route.params.channelId)
	})

	/**
	 * accessId of current channel passed by access endpoint
	 */
	const currentChannelAccessId = ref<string>('')

	/**
	 * number of unread messages in current channel
	 */
	const unreadMessagesCount = computed<number>(() => {
		const computedCount =
			Number(currentChannel.value?.lastMessageIndex) -
			Number(currentChannel.value?.lastSeenMessageIndex)

		if (computedCount > 0) return computedCount

		return 0
	})

	const currentChannelMember = computed<ChannelMember | null>(() => {
		return (
			currentChannel.value?.members?.find((member) => member.isCurrentUser) ||
			null
		)
	})

	/**
	 * message index of first unread message at chat start
	 */
	const newMessagesMarker = ref<number | null>(null)

	/**
	 * are we currently fetching data for chat list?
	 */
	const chatListIsLoading = ref<boolean>(false)

	/**
	 * Are we in active or archive chat list view?
	 */
	const chatViewMode = ref<ChatListType>('active')

	/**
	 * list of all chats
	 */
	const chatList = ref<Channel[]>([])

	/**
	 * returns chat list items based on current chat view mode
	 */
	const chatListByViewMode = computed<Channel[]>(() => {
		const isArchiveViewMode: boolean = chatViewMode.value === 'archive'
		return chatList.value.filter(
			(channel) => channel.isArchived === isArchiveViewMode
		)
	})

	/**
	 * Chat list search
	 */
	const chatListSearchString = ref<string>('')

	/**
	 * auto scroll target state for observer stash + restore
	 *
	 * sessionId === null means "bottom target".
	 * sessionId === undefined means "no restore intent".
	 */
	const autoScrollTarget = ref<{
		sessionId?: number | null
		messageIndex?: number
	}>({})

	let autoScrollTimeout: ReturnType<typeof setTimeout> | null = null

	const scheduleClearAutoScrollState = () => {
		if (autoScrollTimeout) clearTimeout(autoScrollTimeout)
		autoScrollTimeout = setTimeout(() => {
			autoScrollTarget.value = {}
			autoScrollTimeout = null
		}, 1000)
	}

	const restoreAutoScroll = async () => {
		if (
			autoScrollTarget.value.sessionId === undefined &&
			autoScrollTarget.value.messageIndex === undefined
		)
			return

		// if explicit bottom intent has been stored
		if (autoScrollTarget.value.sessionId === null) {
			await handleScroll(false, false)
			return
		}

		await handleScroll(
			false,
			false,
			autoScrollTarget.value.sessionId ?? undefined,
			autoScrollTarget.value.messageIndex
		)
	}

	/**
	 * lets server know that we entered a channel
	 */
	const enterChannel = async () => {
		try {
			const response = await api.post<EnterChannelResponse>(
				`channels/${currentChannelId.value}/access`,
				{
					facilityCode: userStore.appSettings.facilityCode
				}
			)

			const { data } = response

			currentChannelAccessId.value = data.accessId

			try {
				await signalRService.invoke('SubscribeAccess')
			} catch (e) {
				if (e instanceof Error && e.message) {
					toast.error(e.message)
					console.error('SignalR event failed', 'SubscribeAccess', e)
				}
				return Promise.reject(e)
			}

			return Promise.resolve(data)
		} catch (e) {
			console.error(e)
			return Promise.reject(e)
		}
	}

	/**
	 * fetch info about channel
	 */
	const fetchChannel = async () => {
		try {
			const response = await api.get<{ channel: ChannelDetail }>(
				`accesses/${currentChannelAccessId.value}`
			)

			const { data } = response

			const { sessions, ...incommingChannel } = data.channel

			currentChannel.value = {
				...currentChannel.value,
				...incommingChannel,
				sessions: currentChannel.value?.sessions ?? sessions
			}

			return Promise.resolve()
		} catch (e) {
			console.error(e)
			return Promise.reject(e)
		}
	}

	/**
	 * fetches channel, handles scroll and unread messages marker
	 */
	const initChannel = async () => {
		try {
			await fetchChannel()

			if (!currentChannel.value) {
				return Promise.reject()
			}

			// determine new messages marker placement
			if (
				currentChannel.value.lastSeenMessageIndex &&
				currentChannel.value.lastMessageIndex -
					currentChannel.value.lastSeenMessageIndex >
					0
			) {
				newMessagesMarker.value = currentChannel.value.lastSeenMessageIndex + 1
			} else {
				newMessagesMarker.value = null
			}

			await nextRender()

			// determine scroll position (default is bottom)
			let scrollToSession = undefined
			let scrollToMessage = undefined

			if (history.state.scrollToSession || history.state.scrollToMessage) {
				// history state data (e.g. from channel search)
				scrollToSession = history.state.scrollToSession
				scrollToMessage = history.state.scrollToMessage
				history.replaceState({}, '')
			} else if (unreadMessagesCount.value > 0) {
				// first unread message
				scrollToMessage = Number(newMessagesMarker.value)
			}

			handleScroll(false, false, scrollToSession, scrollToMessage)

			// attach observers for sessions
			currentChannel.value.sessions?.forEach((session) => {
				attachSessionObserver(session.id)
			})

			handleLastReadMessage()

			return Promise.resolve()
		} catch (e) {
			console.error(e)
			return Promise.reject(e)
		}
	}

	/**
	 * fetch messages for particular session id
	 * @param sessionId
	 */
	const loadSession = async (sessionId: number) => {
		try {
			const response = await api.get<{ sessions: Session[] }>(
				`accesses/${currentChannelAccessId.value}/messages?sessionIds=${sessionId}`
			)
			const { data } = response

			data.sessions.forEach((incommingSession) => {
				const currentSession = currentChannel.value?.sessions?.find(
					(session) => session.id === incommingSession.id
				)

				if (currentSession) {
					// we know this session, update messages
					currentSession.messages = incommingSession.messages
				} else {
					// we don't know this session, create it
					currentChannel.value?.sessions?.push(incommingSession)
				}
			})

			return Promise.resolve()
		} catch (e) {
			console.error(e)
		}
	}

	/**
	 * updates specific messages in a session
	 * @param messageIdents
	 */
	const updateMessages = async (
		messageIdents?: string[] | number[],
		fromIndex?: number | string
	) => {
		try {
			const query = `${
				messageIdents?.length
					? `indexes=${messageIdents.join(',')}`
					: `fromIndex=${fromIndex}`
			}`

			const response = await api.get<{ sessions: Session[] }>(
				`accesses/${currentChannelAccessId.value}/messages?${query}`
			)
			const { data } = response

			data.sessions.forEach((incommingSession) => {
				const currentSession = currentChannel.value?.sessions?.find(
					(session) => session.id === incommingSession.id
				)

				if (currentSession) {
					// we know this session, update messages
					incommingSession.messages?.forEach((incommingMessage) => {
						const currentMessage = currentSession.messages?.find(
							(message) =>
								message.messageIndex === incommingMessage.messageIndex
						)

						if (currentMessage) {
							// we know this message, update it
							Object.assign(currentMessage, incommingMessage)
						} else {
							// we don't know this message, add it
							currentSession.messages?.push(incommingMessage)
						}
					})
				} else {
					// we don't know this session, load it
					loadSession(incommingSession.id)
				}
			})

			return Promise.resolve()
		} catch (e) {
			console.error(e)
		}
	}

	/**
	 * determines wheter session has loaded messages or not
	 * @param sessionId
	 * @returns
	 */
	const isSessionLoaded = (sessionId: number): boolean => {
		return !!currentChannel.value?.sessions?.find(
			(session) => session.id === sessionId
		)?.messages?.length
	}

	/**
	 * attach intersection observers for session element
	 * lazyload it's messages when observer triggers
	 * @param sessionId
	 */
	const attachSessionObserver = (sessionId: number) => {
		const sessionElement = document.querySelector<HTMLElement>(
			`[data-session-id="${sessionId}"]`
		)

		if (!sessionElement) return

		let timeout: ReturnType<typeof setTimeout> | null = null
		let loaded = false

		const observer = new IntersectionObserver(
			async (entries, observer) => {
				const entry = entries[0]

				if (!entry) return

				if (entry.isIntersecting) {
					// session in viewport

					timeout = setTimeout(async () => {
						// session must be visible for at least 300ms to be loaded
						if (loaded) return

						loaded = true
						await loadSession(sessionId)
						await restoreAutoScroll()
						observer.disconnect()
					}, 300)
				} else {
					// session out of viewport
					if (timeout) {
						clearTimeout(timeout)
						timeout = null
					}
				}
			},
			{
				root: document.querySelector('[data-messenger-scrollwrap]'),
				rootMargin: '75% 0% 75% 0%'
			}
		)

		observer.observe(sessionElement)
	}

	/**
	 * posts message to currently opened channel
	 * @param textMessage
	 */
	const postMessage = async (textMessage: string) => {
		try {
			await api.post<{ createdMessage: Message }>(
				`accesses/${currentChannelAccessId.value}/messages`,
				{
					reactionToMessageIndex: null,
					textMessage: textMessage
				}
			)
		} catch (e) {
			console.error(e)
		}
	}

	/**
	 * Inits upload handshake with the server
	 * retreives info for Blob storage upload
	 * uploads file to blob storage
	 * @param file
	 */
	const postAttachment = async (file: File, text: string) => {
		const { type: contentType, name: fileName, size: fileSize } = file

		try {
			const response = await api.post<{
				sasUri: string
				taskId: string
				webhookEndpoint: string
				webhookSecret: string
			}>(`accesses/${currentChannelAccessId.value}/attachments/uploads`, {
				contentType,
				fileName,
				fileSize,
				text: text
			})

			const { data } = response

			const webhookUrl = `${
				import.meta.env.VITE_BLOB_STORAGE_DATA_TRANSFER_BASE
			}${data.webhookEndpoint}`

			await api.put(data.sasUri, file, {
				skipAuth: true,
				skipDefaultHeaders: true,
				headers: {
					'x-ms-blob-type': 'BlockBlob',
					'Content-Type': contentType,
					'x-ms-meta-webhook_url': webhookUrl,
					'x-ms-meta-webhook_secret': data.webhookSecret
				}
			})

			return Promise.resolve()
		} catch (e) {
			console.error(e)
		}
	}

	const downloadingAttachmentsFileIdents = ref<Set<string>>(new Set())

	/**
	 * Requests temporary SAS URI for downloading file based on fileIdent
	 * response arrives asynchronously via DownloadStatusChanged SignalR message
	 * that initiates downloadAttachment method
	 * @param fileIdent
	 */
	const requestDownloadAttachment = async (fileIdent: string) => {
		try {
			downloadingAttachmentsFileIdents.value.add(fileIdent)

			await api.post<{ downloadId: string }>(
				`accesses/${currentChannelAccessId.value}/attachments/${fileIdent}/downloads`
			)
		} catch (e) {
			console.error(e)
			downloadingAttachmentsFileIdents.value.delete(fileIdent)
		}
	}

	/**
	 * downloads attachment from blob storage via url received by DownloadStatusChanged SignalR message triggered via requestDownloadAttachment method
	 * @param readSasUri
	 */
	const downloadAttachment = async (
		readSasUri: string,
		fileIdent: string,
		fileName: string,
		contentType: string
	) => {
		try {
			const response = await api.get<Blob>(readSasUri, {
				responseType: 'blob',
				skipAuth: true,
				skipDefaultHeaders: true
			})

			const { data } = response

			const url = URL.createObjectURL(
				new Blob([data], {
					type: contentType
				})
			)

			const a = document.createElement('a')
			a.href = url
			a.download = fileName
			a.click()
		} catch (e) {
			console.error(e)
		} finally {
			downloadingAttachmentsFileIdents.value.delete(fileIdent)
		}
	}

	/**
	 * Validates file properties against server accepted rules
	 * @param contentType
	 * @param fileName
	 * @param fileSize
	 */
	const validateAttachment = async (
		contentType: string,
		fileName: string,
		fileSize: number
	) => {
		try {
			const response = await api.post<{ isValid: boolean; message: string }>(
				`accesses/${currentChannelAccessId.value}/attachments/validate`,
				{
					contentType,
					fileName,
					fileSize
				}
			)

			const { data } = response

			return Promise.resolve(data)
		} catch (e) {
			console.error(e)
			return Promise.reject(e)
		}
	}

	/**
	 * Scrolls to selected message
	 * If is scrolled to selected message and the message is overflowing, scroll to the next part
	 * If there's no message to scroll to, scroll all the way down
	 * @param waitForSession should load session of message we're scrolling to first
	 */
	const handleScroll = async (
		waitForSession: boolean,
		smooth: boolean,
		scrollToSession?: number,
		scrollToMessage?: number,
		findActiveHighlight?: boolean
	) => {
		if (
			waitForSession &&
			scrollToSession &&
			!isSessionLoaded(scrollToSession)
		) {
			// wait for session to load before proceeding to scroll
			await loadSession(scrollToSession)
		}

		await nextRender()

		// Keep auto scroll state for fix after async session loading
		if (typeof scrollToMessage === 'number') {
			autoScrollTarget.value = {
				sessionId: scrollToSession,
				messageIndex: scrollToMessage
			}
		} else if (typeof scrollToSession === 'number') {
			autoScrollTarget.value = {
				sessionId: scrollToSession
			}
		} else {
			autoScrollTarget.value = {
				sessionId: null
			}
		}

		scheduleClearAutoScrollState()

		const scrollBehavior = smooth ? 'smooth' : 'instant'

		// define element to scroll to - default is bottom sentinel (scrolls all the way down)
		let elementToScrollTo: HTMLElement | null =
			document.querySelector<HTMLElement>('[data-chat-bottom-sentinel]')

		if (scrollToMessage) {
			// scroll to particular message
			elementToScrollTo = document.querySelector<HTMLElement>(
				`[data-message-index="${scrollToMessage}"]`
			)

			if (findActiveHighlight && elementToScrollTo) {
				// findActiveHighlight scrolls to '.highlight.active' class from search
				elementToScrollTo =
					elementToScrollTo.querySelector<HTMLElement>('.highlight.active')
			}
		} else if (scrollToSession) {
			// scroll to particular session
			elementToScrollTo = document.querySelector<HTMLElement>(
				`[data-session-id="${scrollToSession}"]`
			)
		}

		// trigger scroll
		elementToScrollTo?.scrollIntoView({ behavior: scrollBehavior })
	}

	/**
	 * let's server know that we read the message
	 * @param messageIndex
	 */
	const markMessageAsRead = async (messageIndex: number) => {
		try {
			if (
				!currentChannel.value ||
				(currentChannel.value?.lastSeenMessageIndex &&
					currentChannel.value.lastSeenMessageIndex >= messageIndex)
			) {
				// current lastSeenMessageIndex is greater than this messageIndex
				return
			}

			// update last read and inform server about it
			currentChannel.value.lastSeenMessageIndex = messageIndex
			api.put(`accesses/${currentChannelAccessId.value}/read-status`, {
				messageIndex: messageIndex
			})
		} catch (e) {
			console.error(e)
		}
	}

	/**
	 * gets last read message after scroll stops
	 * @returns
	 */
	const handleLastReadMessage = () => {
		const lastVisible = getLastVisibleMessage(true)
		if (lastVisible) {
			markMessageAsRead(lastVisible)
		}
	}

	/**
	 * @param visibleBottom should return only the message, which bottom (minus threshold) is visible?
	 * @param threshold allowed overflow of last message (px)
	 * @returns
	 */
	const getLastVisibleMessage = (
		visibleBottom: boolean,
		threshold: number = 50
	): number | null => {
		const container = document.querySelector('[data-messenger-scrollwrap]')
		if (!container) return null

		const containerRect = container.getBoundingClientRect()

		const messages = Array.from(
			container.querySelectorAll('[data-message-index]')
		).reverse()

		for (const el of messages) {
			const rect = el.getBoundingClientRect()

			let isVisible: boolean

			if (visibleBottom) {
				isVisible =
					rect.bottom <= containerRect.bottom + threshold &&
					rect.bottom >= containerRect.top
			} else {
				isVisible =
					rect.top < containerRect.bottom && rect.bottom > containerRect.top
			}

			if (isVisible) {
				const messageIndex = Number((el as HTMLElement).dataset.messageIndex)

				return messageIndex
			}
		}

		return null // nothing found
	}

	/**
	 * gets data about available channels
	 * @returns
	 */
	const fetchChannelList = async (useLoader: boolean) => {
		if (useLoader) {
			chatListIsLoading.value = true
		}

		try {
			const response = await api.get<ChannelListResponse>(
				`channels?facilityCode=${userStore.appSettings.facilityCode}`
			)
			const { data } = response

			chatList.value = data.channels
		} catch (e) {
			console.error(e)
		} finally {
			if (useLoader) {
				chatListIsLoading.value = false
			}
		}
	}

	/**
	 * returns paginated results of search in channel list
	 * @param searchString
	 * @param limit
	 * @param startFromTimestamp
	 * @returns
	 */
	const searchChannelList = async (
		searchString: string,
		limit: number,
		startFromTimestamp: string | null
	) => {
		try {
			const response = await api.post<ChannelListSearchResponse>(
				`channels/search/messages`,
				{
					facilityCode: userStore.appSettings.facilityCode,
					searchText: searchString,
					limit: limit,
					startFromTimestamp: startFromTimestamp
				}
			)
			const { data } = response

			return data
		} catch (e) {
			console.error(e)
		}
	}

	/**
	 * returns paginated results of search in channel messages
	 * @param searchString
	 * @param limit
	 * @param startFromTimestamp
	 * @returns
	 */
	const searchChannelMessages = async (
		searchString: string,
		limit: number | null,
		offset: number | null,
		startFromMessageIndex: number | null
	) => {
		try {
			const response = await api.post<ChannelMessagesSearchResponse>(
				`accesses/${currentChannelAccessId.value}/messages/search`,
				{
					searchText: searchString,
					limit: limit,
					offset: offset,
					startFromMessageIndex: startFromMessageIndex
				}
			)
			const { data } = response

			searchChannelMessagesResultItems.value = data.items

			return data
		} catch (e) {
			console.error(e)
		}
	}

	/**
	 * results of channel messages search
	 */
	const searchChannelMessagesResultItems = ref<
		ChannelMessagesSearchResultItem[]
	>([])

	/**
	 * current step of search channel messages
	 */
	const searchChannelMessagesCurrentSearchStep = ref<number>(0)

	/**
	 * returns flat array of searchChannelMessagesResultItems
	 */
	const searchChannelMessagesResultItemsFlat = computed<
		ChannelMessagesSearchResultItemFlat[]
	>(() => {
		const flat = searchChannelMessagesResultItems.value.flatMap((item) => {
			return item.occurrencesInMessage.map((occurance) => {
				return {
					sessionId: item.sessionId,
					messageIndex: item.messageIndex,
					stringStartIndex: occurance.stringStartIndex,
					stringEndIndex: occurance.stringEndIndex
				}
			})
		})

		return flat
	})

	/**
	 * returns map of searchChannelMessagesResultItemsFlat grouped by messageIndex
	 * optimized to avoid expensive filtering operations in components
	 */
	const searchChannelMessagesResultItemsByMessageIndex = computed<
		Record<number, ChannelMessagesSearchResultItemFlat[]>
	>(() => {
		const map: Record<number, ChannelMessagesSearchResultItemFlat[]> = {}

		for (const item of searchChannelMessagesResultItemsFlat.value) {
			if (!map[item.messageIndex]) {
				map[item.messageIndex] = []
			}
			map[item.messageIndex]!.push(item)
		}

		return map
	})

	const createNewChat = async () => {
		const response = await api.post<{ channel: Channel }>(`channels`, {
			workplaceCode: userStore.appSettings.workplaceCode,
			facilityCode: userStore.appSettings.facilityCode,
			problemCategoryCode: userStore.appSettings.problemCategoryCode
		})

		return response.data
	}

	/**
	 * sets chat detail store variables back to default
	 */
	const resetChatView = () => {
		currentChannel.value = null
		currentChannelAccessId.value = ''
		newMessagesMarker.value = null
		chatListIsLoading.value = false
		autoScrollTarget.value = {}
		searchChannelMessagesResultItems.value = []
		searchChannelMessagesCurrentSearchStep.value = 0
	}

	return {
		chatViewMode,
		chatListIsLoading,
		chatListSearchString,
		chatListByViewMode,
		chatList,
		currentChannel,
		currentChannelId,
		currentChannelAccessId,
		unreadMessagesCount,
		newMessagesMarker,
		currentChannelMember,
		searchChannelMessagesResultItems,
		searchChannelMessagesResultItemsByMessageIndex,
		searchChannelMessagesCurrentSearchStep,
		searchChannelMessagesResultItemsFlat,
		fetchChannel,
		initChannel,
		handleScroll,
		fetchChannelList,
		updateMessages,
		searchChannelList,
		isSessionLoaded,
		markMessageAsRead,
		handleLastReadMessage,
		enterChannel,
		postMessage,
		postAttachment,
		downloadingAttachmentsFileIdents,
		requestDownloadAttachment,
		downloadAttachment,
		loadSession,
		createNewChat,
		getLastVisibleMessage,
		searchChannelMessages,
		resetChatView,
		validateAttachment
	}
})
